import {
  Offer,
  Hotel,
  FilterParameters
} from '@findhotel/sapi/dist/types/packages/core/src/types'
import {CancellationPenalty} from 'offer/types/offer'
import {Room} from 'room/types/room'

// TODO (@SAPI): move this type/s to the sapi package and import like Offer and Hotel above instead
export type LovHit = {
  categoryID?: number
  id: number
  objectID: string
  value?: string | Record<string, string>
  name?: string | Record<string, string>
}

export type Lov = Record<string, LovHit>

type AnalyticsContextPayload =
  | MarketingContext
  | SearchContext
  | UserParametersContext
  | HotelContext
  | SearchResultsContext
  | ErrorContext
  | HotelInfoContext
  | PaginationContext
  | PerformanceMeasuredContext
  | SearchHistoryContext
  | AutocompleteContext
  | ExperimentContext
  | CookieConsentContext
  | LeadContext
  | PriceContext
  | CheckoutOfferContext
  | SearchOfferContext
  | BookingContext
  | ServerErrorContext
  | NpsContext

export type AnalyticsContextList = Partial<
  Record<AnalyticsContext, AnalyticsContextPayload>
>

export interface TrackEventPayload {
  category: Category
  entity: Entity
  action: Action
  page?: Page
  payload?: Record<string, any>
  component?: string
  analyticsContext?: AnalyticsContextList
  team?: Team
}

export interface TrackEventProperties extends TrackEventPayload {
  name: string
}

/**
 * Team - The team that trigger the event
 * The team name should match the environment variable name which holds the analytics API key and endpoint URL
 * For example, the `REACT_APP_ANALYTICS_RETENTION_API_KEY` environment variable keeps the API key for
 * the `retention` team.
 * The `Default` is used when the team name is not provided in the trackEvent call. Its value is different for search
 * and checkout projects as each of these projects has its own default team.
 */
export enum Team {
  Default = 'default',
  Retention = 'retention',
  Select = 'select'
}

/**
 * Category - what is triggering the event.
 * Do not add to this list.
 */
export enum Category {
  System = 'System',
  User = 'User',
  Validation = 'Validation'
}

/**
 * Entity - what thing did it involve.
 * Add to this list when a new entity is required.
 */
export enum Entity {
  /**
   * Website wide entities
   */
  Authentication = 'Authentication',
  Booking = 'Booking',
  Errored = 'Errored',
  Experiment = 'Experiment',
  DatePicker = 'DatePicker',
  DatePickerInput = 'DatePickerInput',
  DatePickerDay = 'DatePickerDay',
  DatePickerSubmitButton = 'DatePickerSubmitButton',
  Page = 'Page',
  FetchOptimizelyDataFile = 'FetchOptimizelyDataFile',
  CookieBanner = 'CookieBanner',
  Signup = 'Signup',
  Login = 'Login',
  CookieBannerScript = 'CookieBannerScript',
  FullScreenImageGallery = 'FullScreenImageGallery',
  PasswordlessSend = 'PasswordlessSend',
  Screenshot = 'Screenshot',
  UnlockOverlay = 'UnlockOverlay',
  LoginOverlay = 'LoginOverlay',
  SignupOverlay = 'SignupOverlay',
  OverlayClose = 'OverlayClose',
  NpsSurvey = 'NpsSurvey',
  NpsScore = 'NpsScore',
  AccessControlListDenied = 'AccessControlListDenied',
  AccessControlListAllowed = 'AccessControlListAllowed',
  AccessControlListUnmatched = 'AccessControlListUnmatched',
  ReferralMemberTierUpgrade = 'ReferralMemberTierUpgrade',

  /**
   * Search specific entities
   */
  Search = 'Search',
  Offer = 'Offer',
  LockedOffer = 'LockedOffer',
  SearchSummary = 'SearchSummary',
  SearchBoxSearchButton = 'SearchBoxSearchButton',
  SearchBoxShadow = 'SearchBoxShadow',
  PreferencesOverlay = 'PreferencesOverlay',
  MembershipOverlay = 'MembershipOverlay',
  GuestPicker = 'GuestPicker',
  FilterOptions = 'FilterOptions',
  HotelImage = 'HotelImage',
  HotelDetails = 'HotelDetails',
  HotelDetailsBackButton = 'HotelDetailsBackButton',
  HotelDetailsReviewSummary = 'HotelDetailsReviewSummary',
  HotelDetailsLocationSummary = 'HotelDetailsLocationSummary',
  HotelDetailsFacilitiesSection = 'HotelDetailsFacilitiesSection',
  HotelDetailsSeeMoreFacilities = 'HotelDetailsSeeMoreFacilities',
  HotelDetailsPropertyPolicySection = 'HotelDetailsPropertyPolicySection',
  HotelDetailsPropertyPolicy = 'HotelDetailsPropertyPolicy',
  HotelDetailsTab = 'HotelDetailsTab',
  HotelDetailsGallery = 'HotelDetailsGallery',
  HotelDetailsLocationSection = 'HotelDetailsLocationSection',
  HotelDetailsMapViewInteraction = 'HotelDetailsMapViewInteraction',
  SortByOverlay = 'SortByOverlay',
  DestinationPicker = 'DestinationPicker',
  DestinationPickerInput = 'DestinationPickerInput',
  GuestPickerInput = 'GuestPickerInput',
  GuestPickerSubmitButton = 'GuestPickerSubmitButton',
  PopupOnExit = 'PopupOnExit',
  LoadMoreResultsButton = 'LoadMoreResultsButton',
  SearchResultsEnd = 'SearchResultsEnd',
  SearchResultsPageEnd = 'SearchResultsPageEnd',
  DestinationPickerSearchHistoryItem = 'DestinationPickerSearchHistoryItem',
  DestinationPickerAutocompleteItem = 'DestinationPickerAutocompleteItem',
  SoldOutChangeDatesButton = 'SoldOutChangeDatesButton',
  BackToTopButton = 'BackToTopButton',
  FilterButton = 'FilterButton',
  FloatingSearchControls = 'FloatingSearchControls',
  MapViewButton = 'MapViewButton',
  MapViewToggle = 'MapViewToggle',
  MapViewCloseButton = 'MapViewCloseButton',
  MapViewExpander = 'MapViewExpander',
  MapView = 'MapView',
  MapViewBlankSpace = 'MapViewBlankSpace',
  MapViewHotelPin = 'MapViewHotelPin',
  MapBox = 'MapBox',
  MapViewSearchAreaButton = 'MapViewSearchAreaButton',
  HotelCard = 'HotelCard',
  DetailsOverlayCloseButton = 'DetailsOverlayCloseButton',
  DetailsOverlayShadow = 'DetailsOverlayShadow',
  MapViewHotelImages = 'MapViewHotelImages',
  HotelCardAmenityTags = 'HotelCardAmenityTags',
  DeleteAccountOverlay = 'DeleteAccountOverlay',
  DeleteAccountCancel = 'DeleteAccountCancel',
  DeleteAccountConfirm = 'DeleteAccountConfirm',
  LanguageCurrencyPreferencesButton = 'LanguageCurrencyPreferencesButton',
  CurrencyChange = 'CurrencyChange',
  LanguageChange = 'LanguageChange',
  LanguageCurrencyPreferencesDropdownMenu = 'LanguageCurrencyPreferencesDropdownMenu',
  MagicLinkCode = 'MagicLinkCode',
  MagicLinkSend = 'MagicLinkSend',
  MagicLinkResend = 'MagicLinkResend',
  CongratulationsOverlay = 'CongratulationsOverlay',
  FilterBarFilterButton = 'FilterBarFilterButton',
  FilterPreference = 'FilterPreference',
  FilterBarScrollButton = 'FilterBarScrollButton',
  HotelDetailsShareLinkButton = 'HotelDetailsShareLinkButton',
  HotelDetailsReviewsSection = 'HotelDetailsReviewsSection',
  SortPreference = 'SortPreference',
  SortButton = 'SortButton',
  LoginBanner = 'LoginBanner',

  /**
   * Checkout specific entities
   */
  CancellationTermsLabel = 'CancellationTermsLabel',
  PaymentPage = 'PaymentPage',
  PaymentButton = 'PaymentButton',
  ConfirmationPage = 'ConfirmationPage',
  MembershipActivate = 'MembershipActivate',
  MembershipStatus = 'MembershipStatus',
  MembershipExplore = 'MembershipExplore',
  ItineraryPage = 'ItineraryPage',
  CheckInInstructions = 'CheckInInstructions',
  DueAtProperty = 'DueAtProperty',
  TaxesAndFees = 'TaxesAndFees',
  LocalTax = 'LocalTax',
  ResortFees = 'ResortFees',
  OtherFees = 'OtherFees',
  PropertyFees = 'PropertyFees',
  PersonalDetails = 'PersonalDetails',
  BillingDetails = 'BillingDetails',
  PaymentDetails = 'PaymentDetails',
  Cancellation = 'Cancellation',
  BookAgainButton = 'BookAgainButton',
  ManageBookingButton = 'ManageBookingButton',
  RoomDetailsAndOptions = 'RoomDetailsAndOptions',

  /**
   * Select specific entities
   */
  HotelDetailsStaticOfferCTA = 'HotelDetailsStaticOfferCTA',
  HotelMoreDealsOption = 'HotelMoreDealsOption'
}

/**
 * Action - what did the System/User/Validation do.
 * Do not add to this list unless really necessary.
 */
export enum Action {
  Served = 'Served',
  Clicked = 'Clicked',
  Loaded = 'Loaded',
  Errored = 'Errored',
  Succeeded = 'Succeeded',
  Submitted = 'Submitted',
  Displayed = 'Displayed',
  Viewed = 'Viewed',
  PerformanceMeasured = 'PerformanceMeasured',
  MouseOut = 'MouseOut',
  MouseOvered = 'MouseOvered',
  Scrolled = 'Scrolled',
  Hidden = 'Hidden',
  Requested = 'Requested',
  Duplicated = 'Duplicated'
}

/**
 * Page - where did the event happen.
 * Do not add to this list unless really necessary.
 */
export enum Page {
  Home = 'Home',
  Search = 'Search',
  ProviderRedirect = 'ProviderRedirect',
  Account = 'Account',
  Membership = 'Membership',
  RoomSelection = 'RoomSelection',
  Payment = 'Payment',
  Confirmation = 'Confirmation',
  Itinerary = 'Itinerary',
  Accommodation = 'Accommodation'
}

/**
 * Analytics - what data is required in the event.
 * Add to this list when a new context is required.
 */
export enum AnalyticsContext {
  SegmentContext = 'segmentContext',
  UserParametersContext = 'userParametersContext',
  MarketingContext = 'marketingContext',
  SearchContext = 'searchContext',
  HotelContext = 'hotelContext',
  HotelInfoContext = 'hotelInfoContext',
  OfferContext = 'offerContext',
  SearchResultsContext = 'searchResultsContext',
  AutocompleteContext = 'autocompleteContext',
  SearchHistoryContext = 'searchHistoryContext',
  PerformanceMeasuredContext = 'performanceMeasuredContext',
  PaginationContext = 'paginationContext',
  ErrorContext = 'errorContext',
  ExperimentContext = 'experimentContext',
  CookieConsentContext = 'cookieConsentContext',
  PriceContext = 'priceContext',
  LeadContext = 'leadContext',
  BookingContext = 'bookingContext',
  ServerErrorContext = 'serverErrorContext',
  NpsContext = 'npsContext'
}

export interface MarketingContext {
  label: string
  utmCampaign: string
  utmMedium: string
  utmSource: string
}

export interface SearchContext {
  checkIn: string
  checkOut: string
  isDefaultDates: boolean
  isNoDates: boolean
  currency: string
  locale: string
  rooms: string
  anchorHotelId: string
  placeId: string
  searchId: string
  isUserSearch: boolean
  isHomeSearch: boolean
  isMapOpen: boolean
  isHotelDetailsOpen: boolean
  sortField: string
  sortOrder: string
  preferredRate: number
  filters: FilterParameters
  cugDeals?: string
  mapAreaSearchCoordinates: Array<string | number>
  searchWillRetriggerIfNoOffers: boolean // TODO (@Search) [2022-08-01] Clean up as part of c9457bea-remove-date-check-logic
  isOffersOnlyFetch: boolean
  layout: string
}

export interface UserParametersContext {
  deviceType: string
  userCountry: string
  viewPortHeight: number
  viewPortWidth: number
  anonymousId: string
  deviceTime: string
}

export interface HotelContext {
  hotelId: string
  highestVisiblePrice: number
  hotelPosition: number
  regularPriceRange?: [number, number]
  hotelTags: string[]
  cheapestPrice: number
  isHotelMapOpen: boolean
}

export interface HotelMetaData extends HotelContext {
  topOffers: SearchOfferContext[]
}

export interface PaginationContext {
  pageOffset: number
  pageNumber: number
  pageLength: number
}

export interface SearchResultsContext extends PaginationContext {
  algoliaNbHotels: number
  anchorHotelHasPrivateDeal: boolean
  anchorHotelHasYourChoice: boolean
  hotelIndexesWithPrivateDeals: number[]
  hotelsMetaData: HotelMetaData[]
  nbHotels: number
  staticHotelsLength: number
  staticHotelsOffset: number
  algoliaTotalNbHotels: number
  unavailableHotels: Array<Record<string, unknown>>
}

export interface HotelInfoContext {
  hotelId: string
  hotelName: string
  address: string
  checkInTime: string
  checkOutTime: string
  country: string
  lastBooked: number
  reviewCount: number
  starRating: number
  guestRating: Hotel['guestRating']
  guestType: Hotel['guestType']
  numberOfImages: number
  topAmenities: LovHit[]
}

export interface ErrorContext {
  errorMessage: string
  errorType: string
  errorCode?: string
  errorDetails?: Record<string, unknown>
}
export interface PerformanceMeasuredContext {
  metric: string
  value: number
  unit: string
}

export interface SearchHistoryContext {
  checkIn: string
  checkOut: string
  hotelId?: string
  placeId?: string
  rooms: string
}

export interface AutocompleteContext {
  objectId: string
  position: number
}

export interface ExperimentContext {
  experimentId: string
  variationId: string
  isActive: boolean
}

export interface CookieConsentContext {
  consentId: string
  enabledCookieGroups?: string[]
}

export interface LeadContext {
  anonymousId: string
  checkIn: string
  checkOut: string
  currency: string
  hotelId: string
  hotelPagesSearchId: string
  locale: string
  providerCode: string
  providerRateType: string
  redirectId: string
  rooms: string
}

export interface PriceContext {
  anchorPrice: number
  basePricePerRoomPerNight: number
  chargeableCurrency: string
  chargeableTotalPrice: number
  currency: string
  displayPricePerRoomPerNight: number
  isAnchorPriceShown: boolean
  offerId: string
  otherDueAtProperty: number
  resortFeeDueAtProperty: number
  savingPercentage: number
  savingTotal: number
  taxesDueAtProperty: number
  taxesPerRoomPerNight: number
  totalDueAtProperty: number
  totalPrice: number
  totalUpFront: number
}

export interface BaseOfferContext {
  anchorPrice: number
  availableRooms: number
  bookURI: string
  cancellationPolicy: Offer['cancellationPolicy']
  canPayLater: boolean
  cug: string[]
  displayPricePerRoomPerNight: number
  hasFreeCancellation: boolean
  isPlusDeal: boolean
  isPlusUpgrade: boolean
  isPrivateDeal: boolean
  meals: string[]
  offerId: string
  offerPosition: number
  providerCode: string
  providerFeedId: string
  proxyProviderCode: string
  providerRateType: string
  roomName: Room['name']
  totalPrice: number
}

export interface CheckoutOfferContext extends BaseOfferContext {
  roomId: string
  roomPosition: number
}

export interface SearchOfferContext extends BaseOfferContext {
  displayedTags: string[]
  isAnchorPriceOffer: boolean
  isCheapest: boolean
  isCheapestRAA: boolean
  isSharedRoom: boolean
  isTopOffer: boolean
  offerTags: string[]
  rateBreakdown: Offer['rateBreakdown']
}

export interface BookingContext {
  /**
   * The status of the booking request, possible values are "confirmed", "cancelled" and "error"
   * */
  status: 'confirmed' | 'cancelled' | 'error'
  /**
   * The cancellation penalties applied to the {@link Offer} booked in case of cancellation
   * */
  cancellationPenalties: CancellationPenalty[]
  /**
   * The amount we displayed to the user as the per room per night
   */
  displayPricePerRoomPerNight: number
  /**
   * Whether you have or not free cancellation on your booking
   */
  hasFreeCancellation: boolean
  /**
   * The name of the room booked
   */
  roomName: Room['name']
  /**
   * The total amount the user will pay
   */
  totalPrice: number
  /**
   * It's FH's confirmation ID that can be used by FH customer support.
   * In the BoFH API it is called `confirmationId`.
   * We use a different name so it matches the naming on Looker and EvAS
   */
  bookingId: string
  /**
   * The confirmation ID that a provider needs in their customer support
   */
  providerConfirmationId: string
  /**
   * The ID that FH needs in their customer support
   */
  fhBookingId: string
  /**
   * The entity responsible for handling the user payment
   */
  merchantOfRecord: string
}

export interface ServerErrorContext {
  /**
   * The HTTP status code of the request.
   * https://umbraco.com/knowledge-base/http-status-codes/
   */
  statusCode: number
  /**
   * Which params were passed to the request
   */
  params: Record<string, any>
  /**
   * Which headers were passed to the request
   */
  headers: Record<string, any>
  /**
   * Which url was used in the request
   */
  url: string
  /**
   * Which HTTP method was used for the request
   * https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods
   */
  method: string
  /**
   * An object containing details about the error that happened
   */
  errorResponse: Record<string, any>
  /**
   * A descriptive version of the error that happened
   */
  errorMessage: string
  /**
   * Whether the user will be able to keep using the app of the failing of this request means the appplication
   * is rendered useless.
   */
  recoverableError: boolean
}

export interface NpsContext {
  /**
   * The score given by the user to a NPS survey
   */
  npsScore: number
  /**
   * Represents the question asked to the user after a score is registered. The question usually depends on the score given
   */
  openQuestion?: string
}
